import styled from '@emotion/styled'
import {
  CopyToClipboard,
  TimeAgo,
  UserPersona,
  YesNoIcon,
  YesNothingIcon,
} from 'components'
import { MailYesNoIcon } from 'components/CustomIcons/MailIcons'
import { StatusIcon } from 'components/CustomIcons/StatusIcons'
import { onFormatDate } from 'components/DatePickers'
import { UserPersonaById } from 'components/UserPersona/UserPersona'
import { Box } from 'grid'
import { TFunction } from 'i18next'
import { at } from 'lodash-es'
import { IColumn, IPersonaProps, Link } from 'office-ui-fabric-react'
import * as React from 'react'

const RedBox = styled(Box)`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  right: 3px;
  background: tomato;
`

export type ColumnRenderer<T = any> = (
  item?: T,
  index?: number,
  column?: IColumn
) => any

export const renderJson: ColumnRenderer = (item, i, props) =>
  JSON.stringify(item[props!.fieldName!])
export const renderDefault: ColumnRenderer = (item, i, props) =>
  item[props!.fieldName!]
export const renderPath: ColumnRenderer = (item, i, props) =>
  at(item, [props!.fieldName!])[0] || null

export const renderTimeAgo: ColumnRenderer = (item, i, props) => (
  <TimeAgo date={item[props!.fieldName!]} />
)

export const renderLink: ColumnRenderer = (item, i, props) => (
  <Link href={item[props?.fieldName!]}>{item[props!.fieldName!]}</Link>
)

export const renderPersona: (
  props?: Partial<IPersonaProps>
) => ColumnRenderer = (props) => (item, i, col) => {
  /// TODO Verify wy col might be null, first render? refactor!
  const fieldValue = col ? item[col!.fieldName!] : {}
  if (typeof fieldValue === 'string') {
    return <UserPersonaById personId={fieldValue} {...props} />
  }
  return <UserPersona person={fieldValue} {...props} />
}

export const renderMultiPersonas: (
  props?: Partial<IPersonaProps>
) => ColumnRenderer = (props) => (item, i, col) => {
  return (
    <Box>
      {col ? (
        item[col!.fieldName!].map((person: any, j: any) => (
          <UserPersona key={j} person={person} {...props} />
        ))
      ) : (
        <></>
      )}
    </Box>
  )
}

export const renderNewMailIcon: ColumnRenderer = (item, i, props) => (
  <Box px={1}>
    <MailYesNoIcon value={item.newMails ? item.newMails : 0} />
  </Box>
)

export const renderYesNoIcon: ColumnRenderer = (item, i, props) => (
  <Box px={1}>
    <YesNoIcon value={!!item[props!.fieldName!]} />
  </Box>
)
export const renderYesNothingIcon: ColumnRenderer = (item, i, props) => (
  <Box px={1}>
    <YesNothingIcon value={!!item[props!.fieldName!]} />
  </Box>
)
export const renderStatusIcon: ColumnRenderer = (item, i, props) => (
  <Box px={1}>
    <StatusIcon value={item} />
  </Box>
)
export const renderInvertedYesNoIcon: ColumnRenderer = (item, i, props) => (
  <Box px={1}>
    <YesNoIcon value={!item[props!.fieldName!]} />
  </Box>
)
export const renderMandatory: ColumnRenderer = (item, i, props) => (
  <RedBox px={1} />
)
export const renderCopyable: (renderer?: ColumnRenderer) => ColumnRenderer =
  (renderer = renderDefault) =>
  (item, i, props) =>
    <CopyToClipboard stretch={true}>{renderer(item, i, props)}</CopyToClipboard>

export const renderDate: ColumnRenderer = (item, i, props) => {
  const date = new Date(Date.parse(renderDefault(item, i, props)))

  return renderDefault(item, i, props) !== null ? `${onFormatDate(date)}` : ''
}

export const renderDateTime: ColumnRenderer = (item, i, props) => {
  const date = new Date(Date.parse(renderDefault(item, i, props)))
  return date.toLocaleDateString('de-DE') !== 'Invalid Date'
    ? `${date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })} ${date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : ''
}

export const renderDateFromTicks: ColumnRenderer = (item, i, props) =>
  new Date(renderDefault(item, i, props)).toLocaleDateString()

export const renderEnum: (
  t: TFunction,
  enumName: string,
  emptyElement?: JSX.Element
) => ColumnRenderer =
  (t: TFunction, enumName: string, emptyElement?: JSX.Element) =>
  (item, i, props) => {
    if (at(item, [props!.fieldName!])[0]) {
      return t(`enum.${enumName}.${renderPath(item, i, props)}`)
    } else if (emptyElement) {
      return emptyElement
    }
    return <span />
  }
